<script lang="ts">
	// The design system's StatCard (design/components/surfaces/StatCard.jsx):
	// one number that matters, with its label above and a line below. ledger is
	// money counted, which is why the till's change sits on it.

	import type { Snippet } from 'svelte';

	interface Props {
		value: string;
		label?: string;
		tone?: 'paper' | 'ledger' | 'ink';
		/** Latin digits go in mono, per the artboard. */
		mono?: boolean;
		testId?: string;
		valueTestId?: string;
		children?: Snippet;
	}

	let {
		value,
		label,
		tone = 'paper',
		mono = false,
		testId,
		valueTestId,
		children,
	}: Props = $props();
</script>

<div class="stat {tone}" data-testid={testId}>
	{#if label}
		<p class="label">{label}</p>
	{/if}
	<p class="value" class:mono data-testid={valueTestId}>{value}</p>
	{#if children}
		<p class="body">{@render children()}</p>
	{/if}
</div>

<style>
	.stat {
		border-radius: var(--radius-card);
		padding: var(--card-pad);
	}

	.paper {
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		color: var(--text-strong);
	}

	.ledger {
		background: var(--surface-visibility);
		color: var(--on-ledger);
	}

	.ink {
		background: var(--surface-invert);
		color: var(--on-ink);
	}

	.label {
		margin: 0 0 6px;
		font-family: var(--font-mono);
		font-size: var(--text-micro);
		letter-spacing: var(--tracking-label);
		font-weight: 600;
	}

	.paper .label {
		color: var(--accent-speed);
	}

	.ledger .label {
		color: var(--ledger-mid);
	}

	.ink .label {
		color: var(--speed-on-ink);
	}

	.value {
		margin: 0 0 6px;
		font-size: var(--text-stat);
		font-weight: 700;
		line-height: var(--leading-tight);
		font-family: var(--font-sans);
	}

	.value.mono {
		font-family: var(--font-mono);
	}

	.body {
		margin: 0;
		font-size: var(--text-sm);
		line-height: 1.7;
	}

	.paper .body {
		color: var(--text-body);
	}

	.ledger .body {
		color: var(--ledger-pale);
	}

	.ink .body {
		color: var(--on-ink-body);
	}
</style>
